import CoreIcon from '@components/std-components/CoreIcon'
import { SmallRecommendedIcon } from '@components/std-components/RecommendedIcon'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell/Cell'
import LandscapeProductCard from '@components/UI-Components/LandscapeProductCard'
import { FieldConfig } from '@components/UI-Components/ProductCard'
import { useProductStatus } from '@components/UI-Components/ProductCard/ProductStatusHook'
import ProductCardSkeleton from '@components/UI-Components/ProductCardSkeleton'
import { ProductCardDirection } from '@components/UI-Components/ProductCardSkeleton/ProductCardSkeleton'
import { config } from '@config'
import MdRemove from '@meronex/icons/md/MdRemove'
import {
  ImageAttributes,
  Modules,
  Operations
} from '@modules/common/models/enums/NodeAttributes'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { PRODUCT_REMOVE_LOADER_KEY } from '@modules/order/constants/uiConstants'
import OrderContext from '@modules/order/contexts/order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { ListUIStore } from '@modules/order/stores/view/List/ListUIStore'
import useFilterFieldsByChannel from '@modules/order/utils/filterFieldsByChannel'
import { getMediaCentricURI } from '@services/commonServices'
import { stores } from '@stores'
import buyingSessionGroupStore from '@stores/BuyingSessionGroup'
import { nav } from '@stores/nav'
import { getEnumValue } from '@utils/get-enum-value'
import classNames from 'classnames'
import { useObserver } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import React, { FC, useCallback, useContext, useMemo } from 'react'
import { ProductCardSizes } from '@constants'
import * as css from './ProductInfo.css'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import { View } from '@modules/order/constants/views'

const FieldValueRenderer = ({ value }) =>
  value && (
    <div
      className={classNames(
        'clamp-two-lines',
        css['product-card-info-text-left-aligned']
      )}
    >
      <Typography variant={'body2DarkGrey'} align={'left'} noWrap>
        {value}
      </Typography>
    </div>
  )
const FieldLabelValueRenderer = ({ label, value }) => (
  <div className={'flex column align-self-start'}>
    {label && (
      <div className={css[`product-card-info-field-label-left-aligned`]}>
        <Typography variant={'overlineLightGrey'} align={'left'}>
          {label.toUpperCase()}
        </Typography>
      </div>
    )}
    <div className={css[`product-card-info-field-text-left-aligned`]}>
      <Typography variant={'subtitle1'} align={'left'}>
        {value}
      </Typography>
    </div>
  </div>
)
const ProductTitle = React.memo<{ title }>(({ title }) => {
  return (
    <div className={classNames('clamp-two-lines', css['product-title-left-aligned'])}>
      <Typography variant={'subtitle1'}>{title}</Typography>
    </div>
  )
})

const getProductFieldConfig = (product, priceData, t): FieldConfig[] => [
  {
    id: 'sku',
    value: product?.original?.styleColorCode,
    element: FieldValueRenderer,
    allowedForChannels: [VIPChannel.Retail, VIPChannel.Wholesale]
  },
  {
    id: 'serieDescription',
    value: product?.original?.serieDescription || 'NA',
    element: FieldValueRenderer,
    allowedForChannels: [VIPChannel.Retail, VIPChannel.Wholesale]
  },
  {
    id: 'color',
    value: product?.original?.colorSpecification?.name || 'NA',
    element: FieldValueRenderer,
    allowedForChannels: [VIPChannel.Retail, VIPChannel.Wholesale]
  },
  {
    id: 'wholesalePrice',
    value: priceData?.wholesaleProductPrice
      ? formatCurrencyValueByLocale(
          priceData.whlCurrency,
          priceData.wholesaleProductPrice,
          2
        )
      : '-',
    label: t('SA.productCard.wholesalePrice', 'Wholesale Price'),
    element: FieldLabelValueRenderer,
    allowedForChannels: [
      VIPChannel.Wholesale,
      config.displayWholesalePriceInRetailSHR === 'true' && VIPChannel.Retail
    ]
  },
  {
    id: 'retailPrice',
    value: priceData.retailProductPrice
      ? formatCurrencyValueByLocale(
          priceData.rtlCurrency,
          priceData.retailProductPrice,
          2
        )
      : '',
    label: t('SA.productCard.retailPrice', 'Retail Price'),
    element: FieldLabelValueRenderer,
    allowedForChannels: [VIPChannel.Retail]
  },
  {
    id: 'suggestedRetailPrice',
    value: priceData.retailProductPrice
      ? formatCurrencyValueByLocale(
          priceData.rtlCurrency,
          priceData.retailProductPrice,
          2
        )
      : '-',
    label: t('SA.productCard.localSrp', 'Local SRP'),
    element: FieldLabelValueRenderer,
    allowedForChannels: [VIPChannel.Wholesale]
  }
]

const ProductInfo: FC<CellItem> = ({ rowData }) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const priceData = stores?.productPriceStore?.getPriceForProduct(rowData?.id)
    const viewContextVal = useViewContext()
    const uiStore = viewContextVal.uiStore as ListUIStore
    const {
      uiStore: { isOrderEditable }
    } = useContext(OrderContext)
    const {
      queryParams: { clientType, buyingSessionGroupId, view }
    } = nav
    const isProductActive: boolean = useProductStatus(rowData)

    const productIndex = uiStore.dataStore.itemIndexObjectStore.getIndexByKey(rowData?.id)

    const onBottomIconClickHandler = useCallback(() => {
      uiStore.removeItemObjectFromHierarchy(
        productIndex === -1 ? 0 : productIndex,
        uiStore.getFocusedProductHierarchy(productIndex === -1 ? 0 : productIndex),
        true
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productIndex])

    const deletingInProgress =
      uiStore.dataStore.itemIndexObjectStore.isObjectsDataLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_REMOVE_LOADER_KEY
      )

    const validBSGStatuses = buyingSessionGroupStore.hasStatusesByClientType(
      buyingSessionGroupId,
      VIPClientType[clientType] || VIPClientType.External,
      {
        [VIPChannel.Wholesale]: [
          config.appConfig.enumerations.buyingSessionStatus.BUYING_SESSION_STATUS_VALUES
            .BUYING
        ],
        [VIPChannel.Retail]: [
          config.appConfig.enumerations.retailBuyingSessionStatus
            .BUYING_SESSION_STATUS_VALUES.BUYING
        ]
      }
    )

    const fields = useMemo(
      () => (rowData ? getProductFieldConfig(rowData, priceData, t) : []),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [priceData, rowData, t]
    )
    const channelWisefields = useFilterFieldsByChannel(fields, clientType)
    const url = useMemo(
      () =>
        rowData?.original?.externalImages?.img_Thumbnail ||
        (rowData?.original?.defaultImage
          ? getMediaCentricURI({
              URL: rowData.original.defaultImage,
              Attribute: ImageAttributes.SmallImage,
              Module: Modules.Publisher,
              Operation: Operations.GetFromNode
            })
          : null),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [rowData]
    )
    if (!rowData) {
      // Loader
      return (
        <ProductCardSkeleton
          size={ProductCardSizes.Small}
          direction={ProductCardDirection.Horizontal}
        />
      )
    }

    return (
      <div className={css['product-info-container']}>
        <LandscapeProductCard
          fields={channelWisefields}
          isActive={isProductActive}
          titleRenderer={ProductTitle}
          onClick={e => {
            uiStore.onProductDetailClick(rowData.id)(e)
          }}
          bottomRightIcon={
            deletingInProgress ||
            !validBSGStatuses ||
            (clientType === getEnumValue(VIPClientType.Internal) && rowData?.isCore) ||
            (config.allowAddremoveProductInOrderView === 'false' &&
              view !== View.AddProduct)
              ? null
              : isOrderEditable && (
                  <div className={css.removeBtn}>
                    <MdRemove
                      onClick={onBottomIconClickHandler}
                      style={{
                        display: 'inline-block',
                        height: '60px',
                        width: '60px',
                        padding: '20px'
                      }}
                      size={22}
                    />
                  </div>
                )
          }
          size={ProductCardSizes.Small}
          title={rowData?.original?.style?.modelDescription || 'NA'}
          imageUrl={url}
          disabled={deletingInProgress || !validBSGStatuses}
          topRightIcon={
            clientType === getEnumValue(VIPClientType.External) ? (
              rowData.isRecommended ? (
                <SmallRecommendedIcon />
              ) : null
            ) : rowData.isCore ? (
              <CoreIcon />
            ) : null
          }
        />
      </div>
    )
  })

export default ProductInfo
